import { SearchSource } from '@carvana/merch-contracts';
import { useSubsequentEffect } from '@carvana/merch-react-hooks';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import {
  useFreeTextNoResults,
  useFreeTextSearchQuery,
  useInventoryIsLoaded,
  useSearchRequestId
} from '~/context/srp/InventoryContext';
import SearchEventsDataContext from '~/context/srp/SearchEventsDataContext';
import { useLastSearchRequestId } from './hooks';
import {
  useTrackMegaVehicle,
  useTrackMegaUserInfo,
  useTrackSrpFiltersAndTileCounts
} from './hooks/track';

export default function useTrackResults() {
  const { searchSource, keywordSuggestion } = useContext(
    SearchEventsDataContext
  );
  const freeTextNoResults = useFreeTextNoResults();
  const freeTextSearchQuery = useFreeTextSearchQuery();
  const inventoryIsLoaded = useInventoryIsLoaded();
  const { lastSearchRequestId, updateLastSearchRequestId } =
    useLastSearchRequestId();
  const [shouldFire, setShouldFire] = useState(false);
  const searchRequestId = useSearchRequestId();
  const trackEvent = useTrackEvent();
  const trackUserTileInfo = useTrackMegaUserInfo();
  const trackSrpFiltersAndTileCounts = useTrackSrpFiltersAndTileCounts();
  const {
    loadedInventoryVehicles,
    loadedSimilarVehicles,
    shouldHaveSimilarVehicles,
    svSearchRequestId,
    trackMegaVehicle
  } = useTrackMegaVehicle();
  const lastFreeTextSearchQuery = useRef('');

  useEffect(() => {
    // freeTextNoResults never generates a real searchRequestId (no inventory
    // search runs), so it needs its own trigger alongside the normal
    // searchRequestId-changed detection. Comparing the query text (not just
    // the boolean) ensures back-to-back no-result searches each refire.
    if (!freeTextNoResults) {
      lastFreeTextSearchQuery.current = '';
    }

    const isNewFreeTextQuery =
      freeTextNoResults &&
      freeTextSearchQuery !== lastFreeTextSearchQuery.current;

    if (lastSearchRequestId.current !== searchRequestId || isNewFreeTextQuery) {
      lastFreeTextSearchQuery.current = freeTextSearchQuery;
      setShouldFire(true);
    }
  }, [
    freeTextNoResults,
    freeTextSearchQuery,
    lastSearchRequestId,
    searchRequestId,
    svSearchRequestId,
    setShouldFire
  ]);

  useSubsequentEffect(() => {
    const isStillLoading =
      !loadedInventoryVehicles.isLoaded ||
      (!loadedSimilarVehicles.isLoaded && shouldHaveSimilarVehicles) ||
      !inventoryIsLoaded;

    const isSuggestionSource =
      searchSource === SearchSource.TextSearch ||
      searchSource === SearchSource.RecentSearch ||
      searchSource === SearchSource.SavedSearch;

    if (shouldFire && !isStillLoading) {
      if (!freeTextNoResults) {
        trackMegaVehicle();
      }
      trackUserTileInfo.fireEvent();
      trackSrpFiltersAndTileCounts(loadedInventoryVehicles.vehicles);

      datadogRum.addAction('mega-srp-tile-info', {
        ...trackUserTileInfo.data
      });

      updateLastSearchRequestId(searchRequestId);

      if (isSuggestionSource && !loadedInventoryVehicles.vehicles.length) {
        trackEvent('no-results-from-suggestions', {
          suggestionText: keywordSuggestion?.suggestionText,
          source: searchSource
        });
      }

      if (
        shouldHaveSimilarVehicles &&
        lastSearchRequestId.current !== svSearchRequestId
      ) {
        const similarVehiclesData = {
          ...trackUserTileInfo.data,
          similarVehiclesSearchRequestId: svSearchRequestId,
          vehicles: loadedSimilarVehicles.vehicles
        };

        trackEvent('tile-info-similar-results', similarVehiclesData);
        datadogRum.addAction('tile-info-similar-results', similarVehiclesData);
        updateLastSearchRequestId(svSearchRequestId);
      }

      setShouldFire(false);
    }
  }, [
    freeTextNoResults,
    inventoryIsLoaded,
    keywordSuggestion,
    lastSearchRequestId,
    loadedInventoryVehicles,
    loadedSimilarVehicles,
    searchRequestId,
    searchSource,
    setShouldFire,
    shouldFire,
    shouldHaveSimilarVehicles,
    svSearchRequestId,
    trackEvent,
    trackMegaVehicle,
    trackSrpFiltersAndTileCounts,
    trackUserTileInfo,
    updateLastSearchRequestId
  ]);
}
